export const STT_MODES = ["disabled", "mock", "live"] as const;
export const STT_PROVIDERS = ["deepgram", "speechmatics", "soniox"] as const;
export const SPEECHMATICS_MODELS = ["standard", "enhanced", "melia-1"] as const;
export const SPEECHMATICS_MAX_DELAY_MODES = ["flexible", "fixed"] as const;

export type SessionSttMode = (typeof STT_MODES)[number];
export type SessionSttProvider = (typeof STT_PROVIDERS)[number];
export type SpeechmaticsModel = (typeof SPEECHMATICS_MODELS)[number];
export type SpeechmaticsMaxDelayMode = (typeof SPEECHMATICS_MAX_DELAY_MODES)[number];

export interface SessionSttConfig {
  mode: SessionSttMode;
  provider: SessionSttProvider;
  language: string;
  deepgramModel: string;
  speechmaticsModel: SpeechmaticsModel;
  speechmaticsPreferCurrentSpeaker: boolean;
  speechmaticsSpeakerSensitivity: number;
  speechmaticsMaxSpeakers: number | null;
  speechmaticsMaxDelay: number;
  speechmaticsMaxDelayMode: SpeechmaticsMaxDelayMode;
  speechmaticsEndOfUtteranceSilenceTrigger: number;
  speechmaticsEnablePartials: boolean;
}

export interface SttProviderKeyState {
  deepgram: boolean;
  speechmatics: boolean;
  soniox: boolean;
}

export const DEFAULT_SESSION_STT_CONFIG: SessionSttConfig = {
  mode: "disabled",
  provider: "speechmatics",
  language: "en",
  deepgramModel: "nova-3",
  speechmaticsModel: "enhanced",
  speechmaticsPreferCurrentSpeaker: true,
  speechmaticsSpeakerSensitivity: 0.5,
  speechmaticsMaxSpeakers: null,
  speechmaticsMaxDelay: 2,
  speechmaticsMaxDelayMode: "flexible",
  speechmaticsEndOfUtteranceSilenceTrigger: 0,
  speechmaticsEnablePartials: false,
};

export function normalizeSessionSttConfig(
  input: Partial<SessionSttConfig> | null | undefined,
  fallback: SessionSttConfig = DEFAULT_SESSION_STT_CONFIG,
): SessionSttConfig {
  return {
    mode: parseOneOf(input?.mode, STT_MODES, fallback.mode),
    provider: parseOneOf(input?.provider, STT_PROVIDERS, fallback.provider),
    language: normalizedString(input?.language, fallback.language),
    deepgramModel: normalizedString(input?.deepgramModel, fallback.deepgramModel),
    speechmaticsModel: parseOneOf(
      input?.speechmaticsModel,
      SPEECHMATICS_MODELS,
      fallback.speechmaticsModel,
    ),
    speechmaticsPreferCurrentSpeaker:
      typeof input?.speechmaticsPreferCurrentSpeaker === "boolean"
        ? input.speechmaticsPreferCurrentSpeaker
        : fallback.speechmaticsPreferCurrentSpeaker,
    speechmaticsSpeakerSensitivity: clampNumber(
      input?.speechmaticsSpeakerSensitivity,
      fallback.speechmaticsSpeakerSensitivity,
      0,
      1,
    ),
    speechmaticsMaxSpeakers: normalizeMaxSpeakers(
      input?.speechmaticsMaxSpeakers,
      fallback.speechmaticsMaxSpeakers,
    ),
    speechmaticsMaxDelay: clampNumber(input?.speechmaticsMaxDelay, fallback.speechmaticsMaxDelay, 0.7, 4),
    speechmaticsMaxDelayMode: parseOneOf(
      input?.speechmaticsMaxDelayMode,
      SPEECHMATICS_MAX_DELAY_MODES,
      fallback.speechmaticsMaxDelayMode,
    ),
    speechmaticsEndOfUtteranceSilenceTrigger: clampNumber(
      input?.speechmaticsEndOfUtteranceSilenceTrigger,
      fallback.speechmaticsEndOfUtteranceSilenceTrigger,
      0,
      2,
    ),
    speechmaticsEnablePartials:
      typeof input?.speechmaticsEnablePartials === "boolean"
        ? input.speechmaticsEnablePartials
        : fallback.speechmaticsEnablePartials,
  };
}

export function providerKeyConfigured(
  provider: SessionSttProvider,
  keys: SttProviderKeyState,
): boolean {
  if (provider === "deepgram") return keys.deepgram;
  if (provider === "speechmatics") return keys.speechmatics;
  return keys.soniox;
}

function parseOneOf<T extends string>(value: unknown, allowed: readonly T[], fallback: T): T {
  if (typeof value !== "string") return fallback;
  const normalized = value.trim().toLowerCase();
  return allowed.includes(normalized as T) ? normalized as T : fallback;
}

function normalizedString(value: unknown, fallback: string): string {
  return typeof value === "string" && value.trim() ? value.trim() : fallback;
}

function clampNumber(value: unknown, fallback: number, min: number, max: number): number {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return fallback;
  return Math.min(max, Math.max(min, parsed));
}

function normalizeMaxSpeakers(value: unknown, fallback: number | null): number | null {
  if (value === undefined) return fallback;
  if (value == null || value === "") return null;
  const parsed = Number(value);
  if (!Number.isInteger(parsed) || parsed < 2) return null;
  return parsed;
}
